import { useEffect, useState, type CSSProperties } from 'react';
import { useAuth } from '../context/AuthContext';
import { approvalsApi } from '../lib/api/approvals';
import { attachmentsApi } from '../lib/api/attachments';
import { paymentsApi, type PaymentRequest } from '../lib/api/payments';
import { formatDate, formatVND } from '../lib/format';
import { NHOM_LABEL, VUNG_LABEL, type QcctNhom, type QcctVung } from '../lib/qcct';
import { TypePill } from './StatusBadge';

const CEO_THRESHOLD = 3_000_000;

function amountInWords(amount: number): string {
  const digits = ['không', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín'];
  const readBlock = (value: number, full: boolean) => {
    const hundred = Math.floor(value / 100); const ten = Math.floor((value % 100) / 10); const unit = value % 10;
    const words: string[] = [];
    if (full || hundred) words.push(`${digits[hundred]} trăm`);
    if (ten > 1) words.push(`${digits[ten]} mươi`);
    else if (ten === 1) words.push('mười');
    else if ((full || hundred) && unit) words.push('lẻ');
    if (unit) words.push(unit === 1 && ten > 1 ? 'mốt' : unit === 5 && ten >= 1 ? 'lăm' : digits[unit]);
    return words.join(' ');
  };
  if (!amount) return 'Không đồng';
  const groups: Array<[number, string]> = [[1_000_000_000, 'tỷ'], [1_000_000, 'triệu'], [1_000, 'nghìn'], [1, '']];
  let remaining = Math.round(amount); let output = ''; let started = false;
  for (const [divider, label] of groups) {
    const value = Math.floor(remaining / divider); remaining %= divider;
    if (value || (started && divider === 1)) { output += `${readBlock(value, started)}${label ? ` ${label}` : ''} `; started ||= Boolean(value); }
  }
  return `${output.trim().replace(/^./, (char) => char.toUpperCase())} đồng`;
}

interface Props {
  open: boolean;
  data: PaymentRequest | null;
  onClose: () => void;
  onEdit?: (data: PaymentRequest) => void;
  onDelete?: (data: PaymentRequest) => void;
  onSubmit?: (data: PaymentRequest) => void;
  /** Legacy 1-click disburse (không dùng nữa nếu có onCreateVoucher). */
  onDisburse?: (data: PaymentRequest, proofAttachmentIds: string[]) => Promise<void> | void;
  /** Mở modal Lập phiếu chi. Nếu set, sẽ ưu tiên hơn onDisburse. */
  onCreateVoucher?: (data: PaymentRequest) => void;
  onCancel?: (data: PaymentRequest) => Promise<void> | void;
  onDuplicate?: (data: PaymentRequest) => void;
  onActionDone?: () => Promise<void> | void;
}

const STATUS_TEXT: Record<string, string> = {
  DRAFT: 'Nháp',
  PENDING_NVKT: 'Chờ NV.KT lập PC',
  PENDING_KTT: 'Chờ KTT duyệt',
  PENDING_CEO: 'Chờ CEO ký',
  APPROVED: 'Đã duyệt',
  REJECTED: 'Từ chối',
  DISBURSED: 'Đã chi',
  CANCELLED: 'Đã huỷ',
};

export function PaymentDetailModal({
  open, data, onClose,
  onEdit, onDelete, onSubmit, onDisburse, onCreateVoucher, onCancel, onDuplicate, onActionDone,
}: Props) {
  const { user } = useAuth();
  const [busy, setBusy] = useState<'approve' | 'reject' | 'disburse' | 'cancel' | null>(null);
  const [note, setNote] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [paymentProofs, setPaymentProofs] = useState<File[]>([]);
  const [advancePreviewOpen, setAdvancePreviewOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setNote('');
      setRejectReason('');
      setRejecting(false);
      setPaymentProofs([]);
      setAdvancePreviewOpen(false);
    }
  }, [open, data?.id]);

  if (!open || !data) return null;

  const isOwner = data.requesterId === user?.id;
  const isDraft = data.status === 'DRAFT';
  const isPendingNvkt = data.status === 'PENDING_NVKT';
  const isPendingKtt = data.status === 'PENDING_KTT';
  const isPendingCeo = data.status === 'PENDING_CEO';
  const isApproved = data.status === 'APPROVED';

  const canEditDraft = isDraft && isOwner;
  const canDelete = isOwner && (isDraft || data.status === 'CANCELLED');
  const canEditOrCancel = isOwner && (isDraft || isPendingNvkt || isPendingKtt);
  // Owner có thể cancel khi chưa qua KTT ký (PENDING_NVKT hoặc PENDING_KTT).
  const canCancel = isOwner && (isPendingNvkt || isPendingKtt);

  // NV.KT lập PC ở PENDING_NVKT (flow mới) HOẶC APPROVED (legacy — cho phiếu cũ chưa có PC).
  const canCreateVoucherNvkt = user?.role === 'NV_KT' && isPendingNvkt;
  const canCreateVoucherLegacy = (user?.role === 'NV_KT' || user?.role === 'KTT') && isApproved && !data.cashVoucher;

  const canApproveKTT = user?.role === 'KTT' && isPendingKtt;
  const canApproveCEO = user?.role === 'CEO' && isPendingCeo;
  const canApprove = canApproveKTT || canApproveCEO;

  // "Xác nhận đã chi" chỉ hiện khi PR APPROVED + đã có PC (flow mới, NV.KT đã lập PC ở PENDING_NVKT).
  const canDisburse = isApproved && !!data.cashVoucher && (user?.role === 'NV_KT' || user?.role === 'KTT');
  const isAccountant = user?.role === 'KTT' || user?.role === 'NV_KT' || user?.role === 'CEO';
  const needCeoBadge = data.amount >= CEO_THRESHOLD && (isPendingNvkt || isPendingKtt || isDraft);
  const isUrgent = data.urgency || data.advanceType === 'urgent';

  const resolveApprovalId = async (): Promise<string> => {
    const list = await approvalsApi.list({ status: 'PENDING', limit: 100 });
    const target = list.items.find((a) => a.paymentRequestId === data.id);
    if (!target) throw new Error('Không tìm thấy approval PENDING cho phiếu này');
    return target.id;
  };

  const handleApprove = async () => {
    setBusy('approve');
    try {
      const approvalId = await resolveApprovalId();
      await approvalsApi.approve(approvalId, note || undefined);
      await onActionDone?.();
      onClose();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const handleReject = async () => {
    if (rejectReason.length < 10) {
      alert('Lý do từ chối tối thiểu 10 ký tự');
      return;
    }
    setBusy('reject');
    try {
      const approvalId = await resolveApprovalId();
      await approvalsApi.reject(approvalId, rejectReason);
      await onActionDone?.();
      onClose();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const handleDisburseClick = async () => {
    if (!onDisburse) return;
    if (!paymentProofs.length) {
      alert('Vui lòng tải lên ít nhất một ảnh xác nhận đã chi tiền.');
      return;
    }
    if (!confirm(`Xác nhận đã chi phiếu ${data.code} · ${formatVND(data.amount)}?`)) return;
    setBusy('disburse');
    let uploadedIds: string[] = [];
    try {
      uploadedIds = (await Promise.all(paymentProofs.map((file) => attachmentsApi.upload(file)))).map((file) => file.id);
      await onDisburse(data, uploadedIds);
      onClose();
    } catch (e) {
      await Promise.all(uploadedIds.map((id) => attachmentsApi.remove(id).catch(() => undefined)));
      alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const handleCancelClick = async () => {
    if (!onCancel) return;
    if (!confirm(`Huỷ phiếu ${data.code}? Sau khi huỷ không khôi phục được.`)) return;
    setBusy('cancel');
    try {
      await onCancel(data);
      onClose();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const printAdvanceRequest = () => {
    const popup = window.open('', '_blank');
    if (!popup) { alert('Trình duyệt đang chặn cửa sổ in. Vui lòng cho phép popup rồi thử lại.'); return; }
    popup.opener = null;
    const created = new Date(data.createdAt);
    const date = `${created.getDate()} tháng ${created.getMonth() + 1} năm ${created.getFullYear()}`;
    const safe = (value: string | null | undefined) => (value || '—').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] ?? char));
    popup.document.write(`<!doctype html><html lang="vi"><head><meta charset="utf-8"><title>${safe(data.code)} - Phiếu đề nghị tạm ứng</title><style>
      @page{size:A4 portrait;margin:18mm 16mm} body{font-family:"Times New Roman",serif;color:#000;font-size:14px;line-height:1.35} h1{text-align:center;font-size:20px;margin:0 0 8px} .date{text-align:right;font-style:italic;font-weight:700;margin:0 0 22px}.to{font-style:italic;font-weight:700;margin:0 0 18px}.line{margin:9px 0}.box{border:1px solid #111;padding:10px 12px;margin:10px 0 14px}.grid{display:grid;grid-template-columns:1fr 1fr;gap:8px 24px}.label{font-weight:700}.reason{min-height:42px}.tbl{width:100%;border-collapse:collapse;margin-top:12px}.tbl th,.tbl td{border:1px solid #111;padding:8px;vertical-align:top}.tbl th{text-align:center}.amount{text-align:right;font-weight:700}.total td{font-weight:700}.sign{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-top:44px;text-align:center;font-weight:700}.sign span{display:block;margin-top:62px;font-weight:400;font-style:italic}.muted{font-size:11px;color:#444;margin-top:8px}@media print{.no-print{display:none}}</style></head><body>
      <h1>PHIẾU ĐỀ NGHỊ TẠM ỨNG</h1><div class="date">Ngày ${date}</div>
      <p class="to">Kính gửi: GIÁM ĐỐC CÔNG TY CỔ PHẦN TẬP ĐOÀN CÔNG NGHỆ HITEK</p>
      <div class="box"><div class="grid"><div><span class="label">Họ và tên người đề nghị:</span> ${safe(data.requesterName || data.requester?.name || data.requesterId)}</div><div><span class="label">Bộ phận:</span> ${safe(data.department || data.requester?.departmentName)}</div><div><span class="label">Chi trả cho:</span> ${safe(data.supplier || data.requesterName || data.requester?.name)}</div><div><span class="label">Tài khoản:</span> ${safe(data.bankAccount)}</div></div><div class="line reason"><span class="label">Nội dung tạm ứng:</span> ${safe(data.reason)}</div><div class="line"><span class="label">Số tiền bằng chữ:</span> ${amountInWords(data.amount)}</div></div>
      <table class="tbl"><thead><tr><th style="width:8%">STT</th><th>Nội dung thanh toán</th><th style="width:22%">Số tiền (VND)</th><th style="width:24%">Chứng từ kèm theo</th></tr></thead><tbody><tr><td style="text-align:center">1</td><td>${safe(data.reason)}</td><td class="amount">${formatVND(data.amount)}</td><td>${data.attachments?.length ? `${data.attachments.length} tệp đính kèm` : 'Không'}</td></tr><tr class="total"><td colspan="2">Tổng cộng chi phí</td><td class="amount">${formatVND(data.amount)}</td><td></td></tr></tbody></table>
      <div class="sign"><div>Người đề nghị<span>${safe(data.requesterName || data.requester?.name || '')}</span></div><div>Kế toán trưởng<span></span></div><div>Tổng Giám đốc<span></span></div></div><div class="muted">Mẫu M_KT_DeNghiTamUng · Mã đề nghị: ${safe(data.code)}</div><script>window.onload=()=>{window.print();};<\/script></body></html>`);
    popup.document.close();
  };

  const requesterInitials = (data.requesterName || '?')
    .split(' ')
    .slice(-2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  // Nếu bị từ chối, tra thông tin reject từ history (actor, thời gian, lý do)
  const rejection = data.status === 'REJECTED'
    ? data.history?.find((h) => h.action === 'reject')
    : undefined;
  const rejectedByKtt = rejection?.fromStatus === 'PENDING_KTT';
  const rejectedByCeo = rejection?.fromStatus === 'PENDING_CEO';

  // Timeline steps derive từ status
  const approverRole: 'KTT' | 'CEO' = isPendingCeo || (isApproved && data.amount >= CEO_THRESHOLD) ? 'CEO' : 'KTT';

  if (advancePreviewOpen) {
    return <AdvanceRequestPreview data={data} onClose={() => setAdvancePreviewOpen(false)} onPrint={printAdvanceRequest} />;
  }

  return (
    <div className="adm-backdrop" onClick={onClose}>
      <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
        {/* HERO HEADER */}
        <div className="adm-hero">
          <div className="adm-hero-row1">
            <div className="adm-breadcrumb">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
                <path d="M14 2v5h5" />
              </svg>
              <span>KẾ TOÁN</span>
              <span className="adm-crumb-sep">›</span>
              <span>THANH TOÁN</span>
              <span className="adm-crumb-sep">›</span>
              <span className="adm-crumb-current">CHI TIẾT ĐỀ NGHỊ</span>
            </div>
            <button className="adm-close" onClick={onClose} title="Đóng">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* BODY */}
        <div className="adm-body">
          <div className="adm-grid">
            {/* LEFT COLUMN */}
            <div className="adm-col-left">
              {/* Người đề nghị */}
              <div className="adm-card adm-requester-card">
                <div className="adm-section-label adm-section-label-blue">NGƯỜI ĐỀ NGHỊ</div>
                <div className="adm-requester-body">
                  <div className="adm-requester-avatar">{requesterInitials}</div>
                  <div className="adm-requester-meta">
                    <div className="adm-requester-name">{data.requesterName || data.requesterId}</div>
                    <div className="adm-requester-sub">{data.department || '—'}</div>
                  </div>
                </div>
              </div>

              {/* Thông tin chi */}
              <div className="adm-card">
                <div className="adm-section-label">THÔNG TIN CHI</div>
                <div className="adm-info-grid">
                  <InfoField label="Ngày tạo" value={formatDate(data.createdAt)} />
                  <InfoField label="Ngày gửi duyệt" value={data.submittedAt ? formatDate(data.submittedAt) : '—'} />
                  <InfoField label="Nhà cung cấp" value={data.supplier || '—'} />
                  <InfoField label="TK ngân hàng" value={data.bankAccount || '—'} mono />
                  <InfoField label="Ngân hàng thụ hưởng" value={data.recipientBankName || '—'} />
                  <InfoField label="Chi nhánh" value={data.recipientBankBranch || '—'} />
                </div>
              </div>

              {/* Công tác — chỉ hiển thị khi advancePurpose=congtac */}
              {data.advancePurpose === 'congtac' && (
                <div className="adm-card adm-ct-card">
                  <div className="adm-ct-head">
                    <div className="adm-ct-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect width="20" height="14" x="2" y="7" rx="2" />
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                      </svg>
                    </div>
                    <div className="adm-section-label">CÔNG TÁC · QCCT 2026</div>
                  </div>
                  <div className="adm-info-grid">
                    <InfoField label="Địa điểm" value={data.ctLocation || '—'} />
                    <InfoField label="Số ngày" value={data.ctDays ? `${data.ctDays} ngày` : '—'} />
                    <InfoField
                      label="Nhóm chức danh"
                      value={data.ctNhom ? NHOM_LABEL[data.ctNhom as QcctNhom] : '—'}
                    />
                    <InfoField
                      label="Địa bàn"
                      value={data.ctVung ? VUNG_LABEL[data.ctVung as QcctVung] : '—'}
                    />
                  </div>
                  {data.ctBudgetTotal != null && (
                    <div className="adm-ct-budget-row">
                      <span>Dự toán QCCT</span>
                      <b>{formatVND(data.ctBudgetTotal)}</b>
                      {data.amount > data.ctBudgetTotal && (
                        <span className="adm-ct-exceed">
                          Vượt {formatVND(data.amount - data.ctBudgetTotal)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Timeline */}
              <div className="adm-card">
                <div className="adm-section-label">TIẾN TRÌNH DUYỆT</div>
                <div className="adm-timeline">
                  <TimelineStep
                    state={isDraft ? 'pending' : 'done'}
                    title="Tạo phiếu"
                    sub={`${formatDate(data.createdAt)} · ${data.requesterName || data.requesterId}`}
                  />
                  {!isDraft && (
                    <TimelineStep
                      state="done"
                      title="Gửi duyệt"
                      sub={data.submittedAt ? formatDate(data.submittedAt) : ''}
                    />
                  )}
                  {/* NV.KT lập phiếu chi (stage mới) */}
                  <TimelineStep
                    state={
                      data.status === 'CANCELLED' ? 'upcoming'
                      : isPendingNvkt ? 'pending'
                      : isPendingKtt || isPendingCeo || isApproved || data.status === 'DISBURSED' || data.status === 'REJECTED' || data.cashVoucher
                        ? 'done'
                        : 'upcoming'
                    }
                    title="NV.KT lập phiếu chi"
                    sub={
                      isPendingNvkt
                        ? `Đang chờ · ${Math.max(0, Math.floor((Date.now() - new Date(data.submittedAt || data.createdAt).getTime()) / 3600000))} giờ`
                        : data.cashVoucher
                          ? `${data.cashVoucher.code} · ${data.cashVoucher.createdByName} · ${formatDate(data.cashVoucher.createdAt)}`
                          : ''
                    }
                  />
                  <TimelineStep
                    state={
                      data.status === 'CANCELLED' ? 'upcoming'
                      : rejectedByKtt ? 'rejected'
                      : isPendingKtt ? 'pending'
                      : isPendingCeo || isApproved || data.status === 'DISBURSED' || rejectedByCeo ? 'done'
                      : 'upcoming'
                    }
                    title={rejectedByKtt ? 'KTT từ chối' : 'KTT duyệt'}
                    sub={(() => {
                      if (rejectedByKtt) {
                        return `${rejection?.actorName || ''} · ${rejection ? formatDate(rejection.createdAt) : ''}`;
                      }
                      if (isPendingKtt) {
                        return `Đang chờ · ${Math.max(0, Math.floor((Date.now() - new Date(data.submittedAt || data.createdAt).getTime()) / 3600000))} giờ`;
                      }
                      // KTT đã duyệt (approve) — lấy history mới nhất
                      const kttApprove = [...(data.history || [])]
                        .reverse()
                        .find((h) => h.action === 'approve' && h.actorRole === 'KTT');
                      return kttApprove
                        ? `${kttApprove.actorName} · ${formatDate(kttApprove.createdAt)}`
                        : '';
                    })()}
                  />
                  {(data.amount >= CEO_THRESHOLD) && !rejectedByKtt && (
                    <TimelineStep
                      state={
                        rejectedByCeo ? 'rejected'
                        : isPendingCeo ? 'pending'
                        : isApproved || data.status === 'DISBURSED' ? 'done'
                        : 'upcoming'
                      }
                      title={rejectedByCeo ? 'CEO từ chối' : 'CEO ký duyệt'}
                      sub={(() => {
                        if (rejectedByCeo) {
                          return `${rejection?.actorName || ''} · ${rejection ? formatDate(rejection.createdAt) : ''}`;
                        }
                        if (isPendingCeo) return 'Đang chờ CEO';
                        const ceoApprove = [...(data.history || [])]
                          .reverse()
                          .find((h) => h.action === 'approve' && h.actorRole === 'CEO');
                        return ceoApprove
                          ? `${ceoApprove.actorName} · ${formatDate(ceoApprove.createdAt)}`
                          : '';
                      })()}
                    />
                  )}
                  {data.status !== 'REJECTED' && (
                    <TimelineStep
                      state={data.status === 'DISBURSED' ? 'done' : 'upcoming'}
                      title="Chi tiền + hạch toán"
                      sub={data.disbursedAt ? formatDate(data.disbursedAt) : ''}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="adm-col-right">
              {/* Amount strip (code + số tiền + chips) — moved from hero */}
              <div className="adm-amount-strip">
                <div className="adm-amount-strip-left">
                  <div className="adm-amount-code">{data.code}</div>
                  <div className="adm-hero-amount">{formatVND(data.amount)}</div>
                </div>
                <div className="adm-amount-strip-right">
                  <TypePill type={data.type} advanceType={data.advanceType} />
                  {isUrgent && (isPendingKtt || isPendingCeo) && <span className="apr-badge-khan">Khẩn</span>}
                  {needCeoBadge && <span className="apr-badge-ceo">Cần CEO ký</span>}
                  <span className="adm-hero-status">{STATUS_TEXT[data.status] || data.status}</span>
                </div>
              </div>

              {/* Từ chối banner (khi status = REJECTED) */}
              {data.status === 'REJECTED' && rejection && (
                <div className="adm-reject-banner">
                  <div className="adm-reject-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </div>
                  <div className="adm-reject-body">
                    <div className="adm-reject-title">
                      Phiếu bị từ chối bởi <b>{rejection.actorName}</b>
                      <span className="adm-reject-role">· {rejection.actorRole === 'CEO' ? 'CEO' : rejection.actorRole === 'KTT' ? 'Kế toán trưởng' : rejection.actorRole}</span>
                      <span className="adm-reject-role">· {formatDate(rejection.createdAt)}</span>
                    </div>
                    {rejection.note && (
                      <div className="adm-reject-reason">
                        <span className="adm-reject-reason-label">Lý do:</span>
                        <span className="adm-reject-reason-text">{rejection.note}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Phiếu chi — hiện ngay khi NV.KT lập xong (từ PENDING_KTT trở đi) */}
              {data.cashVoucher && (() => {
                // Tính trạng thái PC theo status của PR
                const pcState =
                  data.status === 'DISBURSED' ? { label: 'Đã chi', bg: '#dcfce7', fg: '#15803d' }
                  : data.status === 'APPROVED' ? { label: 'Đã duyệt · chờ xuất tiền', bg: '#e0e7ff', fg: '#3730a3' }
                  : data.status === 'PENDING_CEO' ? { label: 'Chờ CEO ký', bg: '#dbeafe', fg: '#1e40af' }
                  : data.status === 'PENDING_KTT' ? { label: 'Chờ KTT ký', bg: '#fef3c7', fg: '#a16207' }
                  : data.status === 'REJECTED' ? { label: 'ĐNC bị từ chối', bg: '#fee2e2', fg: '#b91c1c' }
                  : { label: 'Nháp', bg: '#f1f5f9', fg: '#475569' };
                return (
                  <div className="adm-card">
                    <div className="adm-card-head">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={pcState.fg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                        <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
                        <path d="M9 15h6M12 12v6" />
                      </svg>
                      <span className="adm-card-head-title" style={{ color: pcState.fg }}>
                        PHIẾU CHI · {data.cashVoucher.code}
                      </span>
                      <span style={{
                        marginLeft: 8, padding: '2px 8px',
                        background: pcState.bg, color: pcState.fg,
                        fontSize: 11, fontWeight: 600, borderRadius: 9999,
                      }}>
                        {pcState.label}
                      </span>
                    </div>
                    <div className="adm-info-grid" style={{ padding: '0 4px' }}>
                      <InfoField label="Hình thức" value={
                        data.cashVoucher.method === 'tien_mat_vnd' ? 'Tiền mặt VND (1111)'
                        : data.cashVoucher.method === 'chuyen_khoan' ? 'Chuyển khoản (1121)'
                        : 'Tiền mặt USD (1112)'
                      } />
                      <InfoField label="Ngày lập PC" value={formatDate(data.cashVoucher.voucherDate)} />
                      <InfoField label="Người nhận" value={data.cashVoucher.receiverName} />
                      <InfoField label="Số tiền thực chi" value={formatVND(data.cashVoucher.actualAmount)} />
                      <InfoField label="TK Nợ" value={data.cashVoucher.debitAccount} mono />
                      <InfoField label="TK Có" value={data.cashVoucher.creditAccount} mono />
                      {data.cashVoucher.bankAccountUsed && (
                        <InfoField label="TK nguồn" value={data.cashVoucher.bankAccountUsed} />
                      )}
                      {data.cashVoucher.uncNumber && (
                        <InfoField label="Số UNC" value={data.cashVoucher.uncNumber} mono />
                      )}
                    </div>
                    {data.cashVoucher.note && (
                      <div style={{ marginTop: 10, padding: '8px 12px', background: '#f8fafc', borderRadius: 6, fontSize: 13, color: '#334155', fontStyle: 'italic' }}>
                        {data.cashVoucher.note}
                      </div>
                    )}
                    <div style={{ marginTop: 8, fontSize: 11, color: '#64748b' }}>
                      Do <b>{data.cashVoucher.createdByName}</b> lập · {formatDate(data.cashVoucher.createdAt)}
                    </div>
                  </div>
                );
              })()}

              {/* Lý do chi */}
              <div className="adm-card">
                <div className="adm-card-head">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                    <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
                    <path d="M9 9h1M9 13h6M9 17h6" />
                  </svg>
                  <span className="adm-card-head-title">LÝ DO CHI</span>
                </div>
                <div className="adm-reason-text">{data.reason}</div>
              </div>

              {/* Chứng từ đính kèm */}
              <div className="adm-card">
                <div className="adm-card-head adm-card-head-space">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                    </svg>
                    <span className="adm-card-head-title">CHỨNG TỪ ĐÍNH KÈM</span>
                    <span className="adm-card-head-sub">{data.attachments?.length || 0} tệp</span>
                  </div>
                </div>
                {!data.attachments || data.attachments.length === 0 ? (
                  <div className="adm-empty-inline">Không có chứng từ đính kèm</div>
                ) : (
                  <div className="adm-attachment-grid">
                    {data.attachments.map((f) => (
                      <AttachmentCard key={f.id} file={f} />
                    ))}
                  </div>
                )}
              </div>

              {/* Recipients thưởng nóng */}
              {data.type === 'thuong_nong' && data.recipients && data.recipients.length > 0 && (
                <div className="adm-card">
                  <div className="adm-card-head">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round">
                      <path d="M20 12v10H4V12M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                    </svg>
                    <span className="adm-card-head-title">DANH SÁCH NGƯỜI NHẬN ({data.recipients.length})</span>
                  </div>
                  <div className="brp-list" style={{ marginTop: 4 }}>
                    {data.recipients.map((r) => (
                      <div key={r.employeeId} className="brp-row">
                        <div className="brp-avatar">
                          {(r.employeeName || '?').split(' ').slice(-2).map((w) => w[0]).join('').toUpperCase()}
                        </div>
                        <div className="brp-row-meta">
                          <div className="brp-row-name">{r.employeeName}</div>
                        </div>
                        <span className="brp-row-amount">{formatVND(r.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hạch toán dự kiến — KT-only */}
              {isAccountant && (
                <div className="adm-card">
                  <div className="adm-card-head">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round">
                      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                    </svg>
                    <span className="adm-card-head-title">HẠCH TOÁN DỰ KIẾN</span>
                  </div>
                  <div className="pdm-book-entry">
                    <div className="pdm-book-row">
                      <span className="pdm-book-side pdm-book-debit">Nợ</span>
                      <span className="pdm-book-account">6421</span>
                      <span className="pdm-book-name">{data.type === 'thuong_nong' ? 'Chi phí QLDN (Thưởng)' : 'Chi phí quản lý DN'}</span>
                      <span className="pdm-book-amount">{formatVND(data.amount)}</span>
                    </div>
                    <div className="pdm-book-row">
                      <span className="pdm-book-side pdm-book-credit">Có</span>
                      <span className="pdm-book-account">{data.type === 'thuong_nong' ? '334' : '1121'}</span>
                      <span className="pdm-book-name">{data.type === 'thuong_nong' ? 'Phải trả người lao động' : 'Tiền gửi ngân hàng'}</span>
                      <span className="pdm-book-amount">{formatVND(data.amount)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Ghi chú duyệt — chỉ khi approver đang xem phiếu chờ mình duyệt */}
              {canApprove && (
                <div className="adm-card">
                  <div className="adm-card-head adm-card-head-space">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                      <span className="adm-card-head-title">
                        {rejecting ? 'LÝ DO TỪ CHỐI' : 'GHI CHÚ DUYỆT'}
                      </span>
                    </div>
                    <span className="adm-card-head-sub">
                      {(rejecting ? rejectReason : note).length} / 500
                    </span>
                  </div>
                  <div style={{ padding: '14px 18px 16px' }}>
                    <textarea
                      className={`adm-textarea ${rejecting ? 'adm-textarea-danger' : ''}`}
                      rows={3}
                      maxLength={500}
                      value={rejecting ? rejectReason : note}
                      onChange={(e) => rejecting ? setRejectReason(e.target.value) : setNote(e.target.value)}
                      placeholder={rejecting ? 'Nhập lý do từ chối (≥ 10 ký tự)…' : 'VD: OK, đã kiểm chứng từ đầy đủ…'}
                    />
                    <div className="adm-note-hint">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
                      </svg>
                      Ghi chú sẽ hiện trong tiến trình sau khi Duyệt / Trả lại
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="adm-footer">
          <div style={{ display: 'flex', gap: 8 }}>
            {canDelete && onDelete && (
              <button className="adm-btn-return" onClick={() => onDelete(data)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" />
                </svg>
                Xoá phiếu
              </button>
            )}
            {canCancel && onCancel && (
              <button className="adm-btn-return" disabled={busy !== null} onClick={handleCancelClick}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10" /><path d="m15 9-6 6M9 9l6 6" />
                </svg>
                {busy === 'cancel' ? 'Đang huỷ…' : 'Huỷ phiếu'}
              </button>
            )}
          </div>
          <div className="adm-footer-actions">
            {data.type === 'tam_ung' && isAccountant && (
              <button className="adm-btn-ghost" type="button" onClick={() => setAdvancePreviewOpen(true)} title="Xem trước Phiếu đề nghị tạm ứng">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V2h12v7" /><rect width="14" height="8" x="5" y="14" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /></svg>
                Preview đề nghị tạm ứng
              </button>
            )}
            <button className="adm-btn-ghost" onClick={onClose}>Đóng</button>

            {/* Sao chép — mở form Lập ĐNC pre-fill từ phiếu này */}
            {onDuplicate && (
              <button className="adm-btn-ghost" onClick={() => onDuplicate(data)} title="Tạo phiếu mới với nội dung sao chép từ phiếu này">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                  <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                </svg>
                Sao chép
              </button>
            )}

            {/* Owner sửa: cả DRAFT lẫn PENDING_KTT */}
            {canEditOrCancel && onEdit && !canApprove && (
              <button className="adm-btn-ghost" onClick={() => onEdit(data)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                </svg>
                Sửa
              </button>
            )}
            {canEditDraft && onSubmit && !canApprove && (
              <button className="apr-btn-approve-solid adm-btn-approve" onClick={() => onSubmit(data)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                  <path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" />
                </svg>
                Gửi duyệt
              </button>
            )}

            {/* Approver flow: Trả lại + Duyệt */}
            {canApprove && (
              rejecting ? (
                <>
                  <button className="adm-btn-ghost" onClick={() => { setRejecting(false); setRejectReason(''); }}>
                    Huỷ
                  </button>
                  <button
                    className="pay-btn-danger"
                    onClick={handleReject}
                    disabled={busy !== null || rejectReason.length < 10}
                  >
                    {busy === 'reject' ? 'Đang từ chối…' : 'Xác nhận từ chối'}
                  </button>
                </>
              ) : (
                <>
                  <button className="adm-btn-return" onClick={() => setRejecting(true)}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="m9 14 6-6M15 14l-6-6" /><circle cx="12" cy="12" r="10" />
                    </svg>
                    Trả lại
                  </button>
                  <button className="apr-btn-approve-solid adm-btn-approve" onClick={handleApprove} disabled={busy !== null}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                    {busy === 'approve' ? 'Đang duyệt…' : (canApproveKTT && data.amount >= CEO_THRESHOLD) ? 'Duyệt & chuyển CEO' : 'Duyệt'}
                  </button>
                </>
              )
            )}

            {/* NV.KT lập PC (flow mới: PENDING_NVKT → PENDING_KTT) */}
            {canCreateVoucherNvkt && onCreateVoucher && (
              <button className="apr-btn-approve-solid adm-btn-approve" onClick={() => onCreateVoucher(data)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                  <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
                  <path d="M9 15h6M12 12v6" />
                </svg>
                Lập PC & chuyển KTT
              </button>
            )}

            {/* Legacy: NV.KT/KTT lập PC cho phiếu APPROVED cũ chưa có PC */}
            {canCreateVoucherLegacy && onCreateVoucher && (
              <button className="apr-btn-approve-solid adm-btn-approve" onClick={() => onCreateVoucher(data)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                  <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
                  <path d="M9 15h6M12 12v6" />
                </svg>
                Lập phiếu chi
              </button>
            )}

            {/* NV.KT xuất tiền: APPROVED + đã có PC → mark DISBURSED */}
            {canDisburse && onDisburse && (
              <div className="adm-disburse-proof">
                <label>Ảnh xác nhận đã chi <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(event) => setPaymentProofs(Array.from(event.target.files ?? []))} /></label>
                {paymentProofs.length > 0 && <small>{paymentProofs.length} ảnh đã chọn</small>}
                <button className="apr-btn-approve-solid adm-btn-approve" onClick={handleDisburseClick} disabled={busy !== null}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <rect width="20" height="14" x="2" y="5" rx="2" />
                    <line x1="2" x2="22" y1="10" y2="10" />
                  </svg>
                  {busy === 'disburse' ? 'Đang xử lý…' : 'Xác nhận đã chi'}
                </button>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

function InfoField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="adm-info-label">{label}</div>
      <div className="adm-info-value" style={mono ? { fontFamily: "'Roboto Mono', monospace" } : {}}>
        {value}
      </div>
    </div>
  );
}

function TimelineStep({ state, title, sub }: { state: 'done' | 'pending' | 'upcoming' | 'rejected'; title: string; sub: string }) {
  return (
    <div className={`adm-timeline-step adm-timeline-step-${state}`}>
      <div className="adm-timeline-dot">
        {state === 'done' && (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        )}
        {state === 'pending' && (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        )}
        {state === 'rejected' && (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        )}
      </div>
      <div className="adm-timeline-body">
        <div className="adm-timeline-title">{title}</div>
        {sub && <div className="adm-timeline-sub">{sub}</div>}
      </div>
    </div>
  );
}

function AttachmentCard({ file }: { file: { id: string; fileName: string; sizeBytes: number; mimeType: string } }) {
  const ext = file.fileName.split('.').pop()?.toUpperCase() || 'FILE';
  const isPdf = ext === 'PDF';
  const isImg = /^(JPG|JPEG|PNG|GIF|WEBP)$/i.test(ext);
  const tone = isPdf ? 'red' : isImg ? 'blue' : 'gray';
  const sizeKb = file.sizeBytes / 1024;
  const sizeLabel = sizeKb >= 1024 ? `${(sizeKb / 1024).toFixed(1)} MB` : `${sizeKb.toFixed(0)} KB`;

  return (
    <button
      type="button"
      className="adm-attachment-card"
      onClick={() => attachmentsApi.openInNewTab(file.id, file.fileName).catch((e) => alert((e as Error).message))}
      style={{ background: 'none', textAlign: 'left', font: 'inherit', color: 'inherit', padding: 0, cursor: 'pointer' }}
    >
      <div className={`adm-attachment-preview adm-attachment-preview-${tone}`}>
        {isPdf ? (
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <path d="M9 15h6" /><path d="M9 11h2" />
          </svg>
        ) : isImg ? (
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
            <circle cx="9" cy="9" r="2" />
            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
          </svg>
        ) : (
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
        )}
        <span className={`adm-attachment-ext adm-attachment-ext-${tone}`}>{ext}</span>
      </div>
      <div className="adm-attachment-info">
        <div className="adm-attachment-name">{file.fileName}</div>
        <div className="adm-attachment-size">{sizeLabel}</div>
      </div>
    </button>
  );
}

function AdvanceRequestPreview({ data, onClose, onPrint }: { data: PaymentRequest; onClose: () => void; onPrint: () => void }) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  useEffect(() => {
    let current = true;
    let createdUrl: string | null = null;
    setPdfUrl(null); setPdfError(null);
    paymentsApi.advanceRequestPdf(data.id)
      .then((file) => { createdUrl = URL.createObjectURL(file); if (current) setPdfUrl(createdUrl); })
      .catch((error) => { if (current) setPdfError(error instanceof Error ? error.message : 'Không thể tạo PDF.'); });
    return () => { current = false; if (createdUrl) URL.revokeObjectURL(createdUrl); };
  }, [data.id]);
  const created = new Date(data.createdAt);
  const date = `Ngày ${created.getDate()} tháng ${created.getMonth() + 1} năm ${created.getFullYear()}`;
  const requester = data.requesterName || data.requester?.name || data.requesterId;
  const recipient = data.supplier || requester;
  const attachmentText = data.attachments?.length ? data.attachments.map((item) => item.fileName).join(', ') : '—';

  return (
    <div className="adm-backdrop" onClick={onClose} style={{ zIndex: 1200 }}>
      <section onClick={(event) => event.stopPropagation()} style={{ width: 'min(1200px, calc(100vw - 56px))', maxHeight: 'calc(100vh - 48px)', borderRadius: 18, overflow: 'hidden', background: '#eef2f7', boxShadow: '0 24px 70px rgba(15,23,42,.35)', display: 'flex', flexDirection: 'column' }} role="dialog" aria-modal="true" aria-label="Preview phiếu đề nghị tạm ứng">
        <header style={{ background: '#fff', minHeight: 90, padding: '18px 28px', display: 'flex', alignItems: 'center', gap: 14, borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ width: 48, height: 48, display: 'grid', placeItems: 'center', borderRadius: 14, color: '#a16207', background: '#fef3c7' }}>
            <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
          </div>
          <div><h2 style={{ margin: 0, fontSize: 21, color: '#172033' }}>Preview phiếu đề nghị tạm ứng</h2><p style={{ margin: '3px 0 0', color: '#64748b', fontSize: 14 }}>Kiểm tra nội dung trước khi in để trình CEO ký</p></div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
            <button type="button" className="pay-btn-primary" onClick={() => pdfUrl ? window.open(pdfUrl, '_blank') : onPrint()} style={{ height: 46 }} disabled={!pdfUrl && !pdfError}><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V2h12v7" /><rect width="14" height="8" x="5" y="14" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /></svg>In form</button>
            <button type="button" onClick={onClose} aria-label="Đóng preview" style={{ width: 46, height: 46, border: '1px solid #dbe3ee', borderRadius: 10, background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 26, lineHeight: 1 }}>×</button>
          </div>
        </header>
        <div style={{ overflow: 'auto', padding: '46px 34px', background: 'linear-gradient(#e6ebf2,#f4f6f9)' }}>
          {pdfUrl && <iframe src={pdfUrl} title="PDF phiếu đề nghị tạm ứng" style={{ display: 'block', width: 'min(840px, 100%)', height: '1050px', margin: '0 auto', border: 0, background: '#fff', boxShadow: '0 3px 18px rgba(15,23,42,.16)' }} />}
          {pdfError && <div style={{ width: 'min(700px, 100%)', margin: '80px auto', padding: 24, borderRadius: 12, background: '#fff7ed', color: '#9a3412', textAlign: 'center' }}>Không thể tạo PDF: {pdfError}</div>}
          {!pdfUrl && !pdfError && <div style={{ width: 'min(700px, 100%)', margin: '80px auto', padding: 24, borderRadius: 12, background: '#fff', color: '#64748b', textAlign: 'center' }}>Đang tạo PDF từ mẫu đề nghị tạm ứng…</div>}
          <article style={{ display: pdfUrl || pdfError ? 'none' : undefined, width: 'min(840px, 100%)', minHeight: 1050, margin: '0 auto', padding: '64px 70px', boxSizing: 'border-box', background: '#fff', boxShadow: '0 3px 18px rgba(15,23,42,.16)', fontFamily: 'Times New Roman, serif', color: '#050505', fontSize: 17, lineHeight: 1.55 }}>
            <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 16 }}>CÔNG TY CỔ PHẦN TẬP ĐOÀN CÔNG NGHỆ HITEK</div>
            <div style={{ textAlign: 'center', fontSize: 13 }}>L17-11, Tầng 17, Toà Vincom, TP.HCM · MST: 0315456653</div>
            <h1 style={{ textAlign: 'center', fontSize: 31, margin: '38px 0 2px', letterSpacing: '.3px' }}>PHIẾU ĐỀ NGHỊ TẠM ỨNG</h1>
            <div style={{ textAlign: 'center', fontStyle: 'italic', marginBottom: 38 }}>{date}</div>
            <p><b><i>Kính gửi:</i></b> GIÁM ĐỐC CÔNG TY CỔ PHẦN TẬP ĐOÀN CÔNG NGHỆ HITEK</p>
            <p>Họ và tên người đề nghị thanh toán: <b>{requester}</b></p>
            <p>Chi trả cho: <b>{recipient}</b></p>
            <p>Tài khoản: <b>{data.bankAccount || '—'}{data.recipientBankName ? ` — ${data.recipientBankName}${data.recipientBankBranch ? ` · ${data.recipientBankBranch}` : ''}` : ''}</b></p>
            <p>Bộ phận (Hoặc địa chỉ): <b>{data.department || data.requester?.departmentName || '—'}</b></p>
            <p>Nội dung thanh toán: <b>{data.reason}</b></p>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 25, fontSize: 16 }}><thead><tr><th style={previewCell}>STT</th><th style={previewCell}>Nội dung thanh toán</th><th style={previewCell}>Số tiền (VND)</th><th style={previewCell}>Chứng từ kèm theo</th></tr></thead><tbody><tr><td style={{ ...previewCell, textAlign: 'center' }}>1</td><td style={previewCell}>{data.reason}</td><td style={{ ...previewCell, textAlign: 'right' }}>{formatVND(data.amount)}</td><td style={previewCell}>{attachmentText}</td></tr><tr><td style={{ ...previewCell, fontWeight: 700 }} colSpan={2}>Tổng cộng chi phí</td><td style={{ ...previewCell, textAlign: 'right', fontWeight: 700 }}>{formatVND(data.amount)}</td><td style={previewCell} /></tr></tbody></table>
            <p>Đã tạm ứng vào các ngày: <b>0đ</b></p><p>Còn lại: (+) Phải chi thêm / (-) Hoàn ứng còn dư: <b>{formatVND(data.amount)}</b></p><p>Số tiền bằng chữ: <i>{amountInWords(data.amount)}.</i></p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18, marginTop: 74, textAlign: 'center', fontWeight: 700, fontSize: 15 }}><div>Người đề nghị<div style={{ height: 67 }} /><span style={{ fontWeight: 400 }}>{requester}</span></div><div>Kế toán trưởng<div style={{ height: 67 }} /></div><div>Tổng Giám đốc<div style={{ height: 67 }} /></div></div>
            <div style={{ marginTop: 28, fontFamily: 'Arial, sans-serif', color: '#64748b', fontSize: 11 }}>Mẫu M_KT_DeNghiTamUng · Mã đề nghị: {data.code}</div>
          </article>
        </div>
      </section>
    </div>
  );
}

const previewCell: CSSProperties = { border: '1px solid #111', padding: '8px 10px', verticalAlign: 'top' };
